import { lazy, type ComponentType } from "react";

const RELOAD_KEY = "lazy-chunk-reloaded";

/**
 * React.lazy with recovery from stale/failed dynamic imports
 * (happens after a new deploy or dev-server restart invalidates old chunks).
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      const module = await factory();
      window.sessionStorage.removeItem(RELOAD_KEY);
      return module;
    } catch (error) {
      // Retry once after a short delay (transient network/HMR hiccup)
      try {
        await new Promise((resolve) => setTimeout(resolve, 400));
        return await factory();
      } catch (retryError) {
        const alreadyReloaded =
          window.sessionStorage.getItem(RELOAD_KEY) === "true";
        if (!alreadyReloaded) {
          window.sessionStorage.setItem(RELOAD_KEY, "true");
          window.location.reload();
          // Keep the promise pending while the page reloads
          return new Promise<{ default: T }>(() => {});
        }
        throw retryError;
      }
    }
  });
}
